'use client';

import { FC } from 'react';
import { ImportQueueItem } from '@/types/invoice-import';
import { FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const STATUS = {
  pending: {
    icon: FileText,
    color: 'text-gray-400',
    label: 'Pending',
    spin: false,
  },
  processing: {
    icon: Loader2,
    color: 'text-blue-500',
    label: 'Processing',
    spin: true,
  },
  ready: {
    icon: FileText,
    color: 'text-orange-500',
    label: 'Ready',
    spin: false,
  },
  saving: {
    icon: Loader2,
    color: 'text-blue-500',
    label: 'Saving',
    spin: true,
  },
  saved: {
    icon: CheckCircle,
    color: 'text-green-500',
    label: 'Saved',
    spin: false,
  },
  error: {
    icon: AlertCircle,
    color: 'text-red-500',
    label: 'Error',
    spin: false,
  },
} as const;

export const ImportFileList: FC<{
  queue: ImportQueueItem[];
  currentIndex: number;
  onSelect: (index: number) => void;
}> = ({ queue, currentIndex, onSelect }) => (
  <div className="space-y-1">
    {queue.map((item, index) => {
      const { icon: Icon, color, label, spin } = STATUS[item.status];
      const isClickable = item.status === 'ready';

      return (
        <button
          key={item.id}
          type="button"
          onClick={() => isClickable && onSelect(index)}
          disabled={!isClickable}
          className={cn(
            'flex items-center gap-3 w-full px-3 py-2 rounded-md text-left text-sm transition-colors',
            index === currentIndex
              ? 'bg-primary/10 border border-primary/20'
              : 'hover:bg-muted/50',
            !isClickable && 'cursor-default'
          )}
        >
          <Icon
            className={cn('h-4 w-4 shrink-0', color, spin && 'animate-spin')}
          />
          <span className="truncate flex-1">{item.fileName}</span>
          <span className={cn('text-xs shrink-0', color)}>{label}</span>
        </button>
      );
    })}
  </div>
);
